import React, { useState, useEffect } from "react";
import {
  FiBell, FiFilter, FiCheck, FiTrash2,
  FiGift, FiSettings, FiUser, FiUsers, FiClock,
  FiBarChart2, FiZap, FiInfo, FiAlertCircle,
} from "react-icons/fi";
import axios from "axios";
import API_BASE_URL from "../../config/api";
import { useAuth } from "../../context/AuthContext";

interface Notification {
  id: number;
  type: string;
  iconBg?: string; // We'll compute this dynamically
  title: string;
  message: string;
  time: string; // We'll compute "time ago" string
  read: boolean;
  date: string;
}

const timeAgoShort = (dateStr: string): string => {
  const base = new Date(dateStr);
  const diff = Math.floor((Date.now() - base.getTime()) / 60000);
  if (diff < 1) return "just now";
  if (diff < 60) return `${diff} minute${diff > 1 ? "s" : ""} ago`;
  const h = Math.floor(diff / 60);
  if (h < 24) return `${h} hour${h > 1 ? "s" : ""} ago`;
  return `${Math.floor(h / 24)} day${Math.floor(h / 24) > 1 ? "s" : ""} ago`;
};

const getIconBg = (type: string) => {
  switch (type) {
    case "success":
    case "reward":
      return "#ffeaa7";
    case "info":
    case "system":
    case "analytics":
      return "#74b9ff";
    case "warning":
    case "reminder":
      return "#fab1a0";
    case "error":
      return "#ff7675";
    default:
      return "#dfe6e9";
  }
};

const Notifications: React.FC = () => {
  const [filter, setFilter] = useState<"all" | "unread" | "rewards" | "system">("all");
  const [selectedNotifications, setSelectedNotifications] = useState<number[]>([]);
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  
  const { user } = useAuth();

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        // We can pass user/school filters if needed, but for now we fetch all notifications
        // since the model seems to link to schoolId.
        const res = await axios.get(`${API_BASE_URL}/notifications`);
        const data = res.data || [];
        
        const mapped = data.map((n: any) => ({
          id: n.id,
          type: n.type || "info",
          title: n.title,
          message: n.message,
          time: timeAgoShort(n.createdAt),
          date: n.createdAt,
          read: !!n.read,
          iconBg: getIconBg(n.type),
        }));
        
        setAllNotifications(mapped);
      } catch (err) {
        console.error("Failed to fetch notifications", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchNotifications();
  }, []);

  const filteredNotifications = allNotifications.filter((notif) => {
    if (filter === "all") return true;
    if (filter === "unread") return !notif.read;
    if (filter === "rewards") return notif.type === "reward" || notif.type === "success";
    if (filter === "system") return notif.type === "system" || notif.type === "info" || notif.type === "warning";
    return true;
  });

  const getNotificationIcon = (iconKey: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      reward:    <FiGift    size={18} className="text-yellow-600" aria-hidden />,
      success:   <FiCheck   size={18} className="text-green-600"  aria-hidden />,
      system:    <FiSettings size={18} className="text-blue-500"   aria-hidden />,
      info:      <FiInfo     size={18} className="text-blue-500"   aria-hidden />,
      account:   <FiUser    size={18} className="text-purple-500" aria-hidden />,
      community: <FiUsers   size={18} className="text-teal-500"   aria-hidden />,
      reminder:  <FiClock   size={18} className="text-orange-400" aria-hidden />,
      warning:   <FiAlertCircle size={18} className="text-orange-400" aria-hidden />,
      error:     <FiAlertCircle size={18} className="text-red-500"    aria-hidden />,
      analytics: <FiBarChart2 size={18} className="text-blue-500" aria-hidden />,
    };
    return iconMap[iconKey] ?? <FiBell size={18} className="text-gray-400" aria-hidden />;
  };

  const unreadCount = allNotifications.filter((n) => !n.read).length;

  const handleSelectNotification = (id: number) => {
    if (selectedNotifications.includes(id)) {
      setSelectedNotifications(selectedNotifications.filter((nId) => nId !== id));
    } else {
      setSelectedNotifications([...selectedNotifications, id]);
    }
  };

  const handleMarkAllAsRead = async () => {
    alert("All notifications marked as read!");
    // You would typically make an API call here, e.g., PUT /api/notifications/read-all
    setAllNotifications(allNotifications.map((n) => ({ ...n, read: true })));
  };

  const handleDeleteSelected = async () => {
    if (selectedNotifications.length === 0) {
      alert("Please select notifications to delete");
      return;
    }
    if (
      window.confirm(
        `Are you sure you want to delete ${selectedNotifications.length} notification(s)?`
      )
    ) {
      alert("Selected notifications deleted!");
      // API call to delete notifications
      setAllNotifications(allNotifications.filter(n => !selectedNotifications.includes(n.id)));
      setSelectedNotifications([]);
    }
  };

  const handleSelectAll = () => {
    if (selectedNotifications.length === filteredNotifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(filteredNotifications.map((n) => n.id));
    }
  };

  return (
    <div
      style={{
        padding: "24px",
        backgroundColor: "#fafbfc",
        minHeight: "100vh",
        fontFamily:
          '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        maxWidth: "1600px",
        margin: "0 auto",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "#fff",
          borderRadius: "14px",
          padding: "24px 28px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
          marginBottom: "24px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <div>
          <h2 className="text-2xl font-bold m-0">Notifications</h2>
          <p className="text-[#888] mt-1.5 text-sm">
            Stay updated with your activity and important alerts
            {unreadCount > 0 && (
              <span className="ml-2 px-2.5 py-1 bg-[#ff6b6b] text-white rounded-xl text-xs font-semibold">
                {unreadCount} unread
              </span>
            )}
          </p>
        </div>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          {selectedNotifications.length > 0 && (
            <button
              onClick={handleDeleteSelected}
              style={{
                padding: "12px 28px",
                borderRadius: "10px",
                fontSize: "15px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.2s",
                border: "none",
                fontFamily: "inherit",
                background: "#f0f0f0",
                color: "#666",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#e0e0e0";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#f0f0f0";
              }}
            >
              <FiTrash2 /> Delete ({selectedNotifications.length})
            </button>
          )}
          <button
            onClick={handleMarkAllAsRead}
            style={{
              padding: "12px 28px",
              borderRadius: "10px",
              fontSize: "15px",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s",
              border: "none",
              fontFamily: "inherit",
              background: "#00c7b7",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#009e8e";
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow =
                "0 4px 12px rgba(0, 199, 183, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#00c7b7";
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <FiCheck /> Mark All Read
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div
        style={{
          background: "#fff",
          borderRadius: "14px",
          padding: "20px 28px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
          marginBottom: "24px",
          display: "flex",
          gap: "12px",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <FiFilter style={{ color: "#888" }} />
        <div
          style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
            flex: 1,
          }}
        >
          {[
            { value: "all" as const, label: "All" },
            { value: "unread" as const, label: "Unread" },
            { value: "rewards" as const, label: "Rewards" },
            { value: "system" as const, label: "System" },
          ].map((filterOption) => (
            <button
              key={filterOption.value}
              onClick={() => setFilter(filterOption.value)}
              style={{
                padding: "8px 20px",
                borderRadius: "20px",
                border: "2px solid #e0e0e0",
                background: filter === filterOption.value ? "#00c7b7" : "#fff",
                color: filter === filterOption.value ? "#fff" : "#666",
                fontWeight: "600",
                fontSize: "14px",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
            >
              {filterOption.label}
            </button>
          ))}
        </div>
        <button
          onClick={handleSelectAll}
          style={{
            padding: "12px 28px",
            borderRadius: "10px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.2s",
            border: "none",
            fontFamily: "inherit",
            background: "#f0f0f0",
            color: "#666",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#e0e0e0";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#f0f0f0";
          }}
        >
          {selectedNotifications.length === filteredNotifications.length
            ? "Deselect All"
            : "Select All"}
        </button>
      </div>

      {/* Notifications List */}
      <div
        style={{
          background: "#fff",
          borderRadius: "14px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
          overflow: "hidden",
        }}
      >
        {filteredNotifications.length > 0 ? (
          filteredNotifications.map((notif, index) => (
            <div
              key={notif.id}
              style={{
                display: "flex",
                gap: "16px",
                borderBottom:
                  index < filteredNotifications.length - 1
                    ? "1px solid #f0f0f0"
                    : "none",
                background: notif.read ? "#fff" : "#f9fbff",
                cursor: "pointer",
                padding: "20px 28px",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f9f9f9")}
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = notif.read ? "#fff" : "#f9fbff")
              }
            >
              {/* Checkbox */}
              <input
                type="checkbox"
                checked={selectedNotifications.includes(notif.id)}
                onChange={() => handleSelectNotification(notif.id)}
                style={{
                  width: "18px",
                  height: "18px",
                  cursor: "pointer",
                  marginTop: "2px",
                }}
                onClick={(e) => e.stopPropagation()}
              />

              {/* Icon */}
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  background: notif.iconBg,
                }}
              >
                {getNotificationIcon(notif.type)}
              </div>

              {/* Content */}
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    marginBottom: "4px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      color: "#222",
                    }}
                  >
                    {notif.title}
                  </div>
                  {!notif.read && (
                    <span
                      style={{
                        width: "8px",
                        height: "8px",
                        background: "#00c7b7",
                        borderRadius: "50%",
                        flexShrink: 0,
                        marginLeft: "12px",
                        marginTop: "6px",
                      }}
                    />
                  )}
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    color: "#666",
                    lineHeight: "1.5",
                    marginBottom: "6px",
                  }}
                >
                  {notif.message}
                </div>
                <div style={{ fontSize: "12px", color: "#999" }}>
                  {notif.time}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "60px 20px",
              color: "#999",
            }}
          >
            <div
              style={{
                fontSize: "64px",
                marginBottom: "16px",
                opacity: 0.5,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <FiBell size={64} color="#ccc" aria-hidden />
            </div>
            <div
              style={{
                fontSize: "20px",
                fontWeight: "600",
                color: "#666",
                marginBottom: "8px",
              }}
            >
              No notifications
            </div>
            <div style={{ fontSize: "15px", color: "#999" }}>
              You're all caught up! Check back later for updates.
            </div>
          </div>
        )}
      </div>

      {/* Notification Settings Info */}
      <div className="bg-[#f0f9ff] rounded-[14px] p-6 mt-6 flex items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
          <FiInfo size={20} className="text-blue-600" aria-label="Tip" />
        </div>
        <div className="flex-1">
          <div className="text-base font-semibold text-[#0369a1] mb-1.5">
            Manage Your Notification Preferences
          </div>
          <div className="text-sm text-[#0284c7] leading-relaxed">
            You can customize which notifications you receive by going to your profile settings.
            Choose what's most important to you!
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;

